import type { TenantCompanyProfile } from '@forq/platform-data';

/**
 * Artikelblatt (Stammblatt) als druckfertiges HTML im FTW-Layout -- die Artikelakte auf einem Blatt:
 * Stammdaten, Preise (EK nur mit Berechtigung), Lagerbestand je Lagerplatz und die Lieferanten, die
 * den Artikel führen. Gleicher Briefkopf/Fußzeile wie die übrigen Belege (aus dem Firmenprofil).
 * Gedruckt/als PDF gespeichert wird über den Browser (Button im erzeugten HTML).
 */

export interface ArticleSheetStockLocation {
  readonly code: string;
  readonly name: string;
  readonly onHand: number;
  readonly available: number;
  readonly minimumStock: number;
}
export interface ArticleSheetSupplier {
  readonly supplierNumber: string;
  readonly supplierName: string;
  readonly supplierItemNumber: string;
  readonly purchasePrice: number;
  readonly leadTimeDays: number | null;
  readonly preferred: boolean;
}
export interface ArticleSheetContext {
  readonly company: TenantCompanyProfile;
  readonly todayIso: string;
  readonly canViewPurchasePrices: boolean;
  readonly item: {
    readonly isService: boolean;
    readonly number: string;
    readonly name: string;
    readonly description: string;
    readonly unit: string;
    readonly salesPrice: number;
    readonly purchasePrice: number;
    readonly vatRate: number;
    readonly manufacturer: string;
    readonly manufacturerPartNumber: string;
    readonly barcode: string;
    readonly stockTracked: boolean;
    readonly minimumStock: number;
    readonly active: boolean;
    readonly activeSince: string;
    readonly preferredSupplierName: string;
  };
  readonly stock: {
    readonly onHand: number;
    readonly available: number;
    readonly reserved: number;
    readonly locations: readonly ArticleSheetStockLocation[];
  } | null;
  readonly suppliers: readonly ArticleSheetSupplier[];
}

export interface SupplierSheetItem {
  readonly itemNumber: string;
  readonly itemName: string;
  readonly supplierItemNumber: string;
  readonly purchasePrice: number;
  readonly leadTimeDays: number | null;
  readonly preferred: boolean;
}
export interface SupplierSheetContext {
  readonly company: TenantCompanyProfile;
  readonly todayIso: string;
  readonly canViewPurchasePrices: boolean;
  readonly supplier: {
    readonly number: string;
    readonly name: string;
    readonly contactName: string;
    readonly email: string;
    readonly phone: string;
    readonly website: string;
    readonly vatId: string;
    readonly street: string;
    readonly postalCode: string;
    readonly city: string;
    readonly countryCode: string;
    readonly active: boolean;
    readonly activeSince: string;
    readonly notes: string;
  };
  readonly items: readonly SupplierSheetItem[];
}

// --- Formatierung -----------------------------------------------------------

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
function money(value: number): string { return euro.format(value); }
function dateLabel(value: string): string {
  const [year, month, day] = (value || '').split('-');
  return year && month && day ? `${day}.${month}.${year}` : (value || '–');
}
function esc(value: string): string {
  return (value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

// --- Briefkopf / Fußzeile (aus dem Firmenprofil) ---------------------------

function letterhead(company: TenantCompanyProfile): string {
  const identity = company.identity;
  const lh = company.letterhead;
  const slotLeft = lh.logoDataUrl
    ? `<img src="${esc(lh.logoDataUrl)}" alt="${esc(identity.name)}">`
    : `<div class="logo-name">${esc(identity.name || 'FORQ')}</div>`;
  const slotCenter = lh.logoCenterDataUrl ? `<img src="${esc(lh.logoCenterDataUrl)}" alt="">` : '';
  const slotRight = lh.logoRightDataUrl
    ? `<img src="${esc(lh.logoRightDataUrl)}" alt="">`
    : `<div class="services">Handel&nbsp;&nbsp;Service<br>Ersatzteile&nbsp;&nbsp;Vermietung<br><span class="red">für Flurförderzeuge</span></div>`;
  return `
    <div class="hd">
      <div class="hd-slot hd-left">${slotLeft}</div>
      <div class="hd-slot hd-center">${slotCenter}</div>
      <div class="hd-slot hd-right">${slotRight}</div>
    </div>
    <hr class="hd-rule">`;
}

function footer(company: TenantCompanyProfile): string {
  const { identity, contact, bank } = company;
  const col = (heading: string, lines: (string | false)[]) =>
    `<div><span class="fh">${esc(heading)}</span>${lines.filter(Boolean).map((l) => esc(l as string)).join('\n')}</div>`;
  return `<div class="footer">
    ${col(identity.name || 'Firma', [identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()])}
    ${col('Kontakt', [contact.phone && `Tel.: ${contact.phone}`, contact.fax && `Fax: ${contact.fax}`, contact.email, contact.website])}
    ${col('Bankverbindung', [bank.bankName, bank.iban && `IBAN: ${bank.iban}`, bank.bic && `BIC: ${bank.bic}`])}
    ${col('Unternehmen', [identity.managingDirectors && `Geschäftsführer: ${identity.managingDirectors}`, identity.registerCourt && identity.register && `${identity.registerCourt} ${identity.register}`, identity.vatId && `USt-IdNr.: ${identity.vatId}`])}
  </div>`;
}

// --- Belegkörper ------------------------------------------------------------

function row(label: string, value: string): string {
  return `<tr><th>${esc(label)}</th><td>${esc(value)}</td></tr>`;
}

function body(ctx: ArticleSheetContext): string {
  const { item, stock, suppliers, canViewPurchasePrices } = ctx;
  const margin = item.salesPrice > 0 && item.purchasePrice > 0 ? item.salesPrice - item.purchasePrice : null;

  const stammHtml = `<h2 class="sec">Stammdaten</h2>
    <table class="box">
      ${row(item.isService ? 'Leistungsnummer' : 'Artikelnummer', item.number)}
      ${row('Bezeichnung', item.name)}
      ${row('Einheit', item.unit)}
      ${row('Status', item.active ? 'Aktiv' : 'Inaktiv')}
      ${row('Im Bestand seit', dateLabel(item.activeSince))}
      ${item.manufacturer ? row('Hersteller', item.manufacturer) : ''}
      ${item.manufacturerPartNumber ? row('Hersteller-Nr.', item.manufacturerPartNumber) : ''}
      ${item.barcode ? row('Barcode', item.barcode) : ''}
      ${item.preferredSupplierName ? row('Bevorzugter Lieferant', item.preferredSupplierName) : ''}
    </table>`;

  // Einkauf/Marge sind bei Dienstleistungen ohne Aussage (EK ist immer 0) -- weglassen.
  const priceHtml = `<h2 class="sec">Preise</h2>
    <table class="box">
      ${canViewPurchasePrices && !item.isService ? row('Einkaufspreis', money(item.purchasePrice)) : ''}
      ${row('Verkaufspreis', money(item.salesPrice))}
      ${canViewPurchasePrices && !item.isService && margin !== null ? row('Marge', money(margin)) : ''}
      ${row('Umsatzsteuer', `${item.vatRate} %`)}
    </table>`;

  const stockHtml = item.stockTracked ? `<h2 class="sec">Lagerbestand</h2>
    <div class="kpis">
      <div><span>Bestand</span><strong class="${stock && stock.onHand <= item.minimumStock ? 'is-open' : ''}">${stock ? stock.onHand : 0} ${esc(item.unit)}</strong></div>
      <div><span>Verfügbar</span><strong>${stock ? stock.available : 0} ${esc(item.unit)}</strong></div>
      <div><span>Reserviert</span><strong>${stock ? stock.reserved : 0} ${esc(item.unit)}</strong></div>
      <div><span>Mindestbestand</span><strong>${item.minimumStock} ${esc(item.unit)}</strong></div>
    </div>
    ${stock && stock.locations.length > 0 ? `<table class="grid">
      <tr><th>Lagerplatz</th><th>Bestand</th><th>Verfügbar</th><th>Mindest</th></tr>
      ${stock.locations.map((location) => `<tr><td>${esc(location.name)} <em>(${esc(location.code)})</em></td><td>${location.onHand}</td><td>${location.available}</td><td>${location.minimumStock}</td></tr>`).join('')}
    </table>` : ''}` : '';

  const supplierHtml = suppliers.length > 0 ? `<h2 class="sec">Lieferanten</h2>
    <table class="grid">
      <tr><th>Lieferant</th><th>Lieferanten-Nr.</th>${canViewPurchasePrices ? '<th>Preis</th>' : ''}<th>Lieferzeit</th><th>Bevorzugt</th></tr>
      ${suppliers.map((supplier) => `<tr><td>${esc(supplier.supplierName)} <em>(${esc(supplier.supplierNumber)})</em></td><td>${esc(supplier.supplierItemNumber || '–')}</td>${canViewPurchasePrices ? `<td>${money(supplier.purchasePrice)}</td>` : ''}<td>${supplier.leadTimeDays === null ? '–' : `${supplier.leadTimeDays} Tage`}</td><td>${supplier.preferred ? 'Ja' : 'Nein'}</td></tr>`).join('')}
    </table>` : '';

  const notesHtml = item.description ? `<h2 class="sec">Beschreibung</h2><p class="note">${esc(item.description)}</p>` : '';

  return `<div class="cols">
      <div class="col">${stammHtml}</div>
      <div class="col">${priceHtml}</div>
    </div>
    ${stockHtml}
    ${supplierHtml}
    ${notesHtml}`;
}

function renderSheet(opts: {
  readonly company: TenantCompanyProfile;
  readonly docTitle: string;
  readonly titleTag: string;
  readonly metaRows: readonly (readonly [string, string])[];
  readonly name: string;
  readonly bodyHtml: string;
}): string {
  const { company, docTitle, titleTag, metaRows, name, bodyHtml } = opts;
  const accent = company.letterhead.accentColor || '#e30613';
  const meta = `
    <div class="doc-head">
      <div class="doc-title">${esc(docTitle)}</div>
      <table class="meta">
        ${metaRows.map((entry) => `<tr><th>${esc(entry[0])}</th><td>${esc(entry[1])}</td></tr>`).join('')}
      </table>
    </div>`;
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>${esc(titleTag)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); position: relative; }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 10mm; }
  .sender { font-size: 12px; color: #444; font-weight: 700; }
  .doc-head { min-width: 70mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 17px; font-weight: 800; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  h1.name { font-size: 22px; font-weight: 800; color: var(--accent); margin: 8mm 0 0; }
  .band-rule { border: 0; border-top: 1px solid #bbb; margin: 6px 0 0; }
  h2.sec { font-size: 13.5px; font-weight: 700; margin: 6mm 0 2.5mm; color: #111; }
  .cols { display: flex; gap: 8mm; align-items: flex-start; }
  .cols .col { flex: 1; min-width: 0; }
  table.box { width: 100%; border-collapse: collapse; border: 1px solid #ccc; }
  table.box th { text-align: left; color: var(--label); font-weight: 700; padding: 5px 8px; width: 46%; vertical-align: top; border: 1px solid #e2e2e2; background: #fafafa; }
  table.box td { padding: 5px 8px; vertical-align: top; border: 1px solid #e2e2e2; }
  table.grid { width: 100%; border-collapse: collapse; border: 1px solid #ccc; margin-top: 2mm; }
  table.grid th { text-align: left; color: var(--label); font-weight: 700; padding: 4px 8px; border: 1px solid #e2e2e2; background: #fafafa; }
  table.grid td { padding: 4px 8px; border: 1px solid #e2e2e2; vertical-align: top; }
  table.grid em { color: var(--accent); font-style: normal; font-size: 9px; }
  .kpis { display: flex; gap: 4mm; }
  .kpis div { flex: 1; border: 1px solid #e2e2e2; border-radius: 4px; padding: 6px 8px; background: #fafafa; }
  .kpis span { display: block; color: #888; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; }
  .kpis strong { font-size: 15px; }
  .kpis strong.is-open { color: var(--accent); }
  .note { font-size: 10px; color: #444; margin: 3mm 0; text-align: justify; white-space: pre-line; }
  .footer { margin-top: 12mm; border-top: 1.5px solid var(--accent); padding-top: 5px; display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
    h2.sec { break-after: avoid; break-inside: avoid; }
    table.box, table.grid, .kpis { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    ${letterhead(company)}
    <div class="band"><div class="sender">${esc(company.identity.name || '')}</div>${meta}</div>
    <hr class="band-rule">
    <h1 class="name">${esc(name)}</h1>
    ${bodyHtml}
    ${footer(company)}
  </div>
</body></html>`;
}

/** Das vollständige HTML des Artikel- bzw. Leistungsblatts -- Anzeige im iframe (srcDoc) + Drucken. */
export function renderArticleSheetHtml(ctx: ArticleSheetContext): string {
  const blattLabel = ctx.item.isService ? 'Leistungsblatt' : 'Artikelblatt';
  return renderSheet({
    company: ctx.company,
    docTitle: ctx.item.isService ? 'LEISTUNGSBLATT' : 'ARTIKELBLATT',
    titleTag: `${blattLabel} ${ctx.item.number}`,
    metaRows: [[ctx.item.isService ? 'Leistung:' : 'Artikel:', ctx.item.number], ['Datum:', dateLabel(ctx.todayIso)], ['Status:', ctx.item.active ? 'Aktiv' : 'Inaktiv']],
    name: ctx.item.name,
    bodyHtml: body(ctx),
  });
}

function supplierBody(ctx: SupplierSheetContext): string {
  const { supplier, items, canViewPurchasePrices } = ctx;
  const preferred = items.filter((entry) => entry.preferred).length;
  const leads = items.map((entry) => entry.leadTimeDays).filter((value): value is number => value !== null);
  const avgLead = leads.length ? Math.round(leads.reduce((sum, value) => sum + value, 0) / leads.length) : null;

  const stammHtml = `<h2 class="sec">Stammdaten</h2>
    <table class="box">
      ${row('Lieferantennummer', supplier.number)}
      ${row('Name', supplier.name)}
      ${row('Status', supplier.active ? 'Aktiv' : 'Inaktiv')}
      ${row('Im Bestand seit', dateLabel(supplier.activeSince))}
      ${supplier.vatId ? row('USt-IdNr.', supplier.vatId) : ''}
    </table>`;

  const kpiHtml = `<h2 class="sec">Kennzahlen</h2>
    <div class="kpis">
      <div><span>Verknüpfte Artikel</span><strong>${items.length}</strong></div>
      <div><span>Bevorzugt</span><strong>${preferred}</strong></div>
      <div><span>Ø Lieferzeit</span><strong>${avgLead === null ? '–' : `${avgLead} Tage`}</strong></div>
    </div>`;

  const contactHtml = `<h2 class="sec">Kontakt &amp; Anschrift</h2>
    <table class="box">
      ${row('Ansprechpartner', supplier.contactName || '–')}
      ${row('E-Mail', supplier.email || '–')}
      ${row('Telefon', supplier.phone || '–')}
      ${supplier.website ? row('Web', supplier.website) : ''}
      ${row('Anschrift', [supplier.street, `${supplier.postalCode} ${supplier.city}`.trim(), supplier.countryCode].filter(Boolean).join(', ') || '–')}
    </table>`;

  const itemsHtml = items.length > 0 ? `<h2 class="sec">Gelieferte Artikel</h2>
    <table class="grid">
      <tr><th>Artikel</th><th>Lieferanten-Nr.</th>${canViewPurchasePrices ? '<th>Preis</th>' : ''}<th>Lieferzeit</th><th>Bevorzugt</th></tr>
      ${items.map((entry) => `<tr><td>${esc(entry.itemName)} <em>(${esc(entry.itemNumber)})</em></td><td>${esc(entry.supplierItemNumber || '–')}</td>${canViewPurchasePrices ? `<td>${money(entry.purchasePrice)}</td>` : ''}<td>${entry.leadTimeDays === null ? '–' : `${entry.leadTimeDays} Tage`}</td><td>${entry.preferred ? 'Ja' : 'Nein'}</td></tr>`).join('')}
    </table>` : '';

  const notesHtml = supplier.notes ? `<h2 class="sec">Bemerkungen</h2><p class="note">${esc(supplier.notes)}</p>` : '';

  return `<div class="cols">
      <div class="col">${stammHtml}${kpiHtml}</div>
      <div class="col">${contactHtml}</div>
    </div>
    ${itemsHtml}
    ${notesHtml}`;
}

/** Das vollständige HTML des Lieferantenblatts -- zum Anzeigen im iframe (srcDoc) und Drucken. */
export function renderSupplierSheetHtml(ctx: SupplierSheetContext): string {
  return renderSheet({
    company: ctx.company,
    docTitle: 'LIEFERANTENBLATT',
    titleTag: `Lieferantenblatt ${ctx.supplier.number}`,
    metaRows: [['Lieferant:', ctx.supplier.number], ['Datum:', dateLabel(ctx.todayIso)], ['Status:', ctx.supplier.active ? 'Aktiv' : 'Inaktiv']],
    name: ctx.supplier.name,
    bodyHtml: supplierBody(ctx),
  });
}
